import { useSelector } from 'react-redux';
import { AuthReducerState } from '../interfaces';
import { store } from '../store';
import { warn } from './commonUtils';

export function getAuth(): AuthReducerState {
  return store.getState().authReducer;
}

// In general you want to select the piece of state you need.
// This avoids unnecessary re-renders, but since the auth state
// is only updated once at the start of the app, it's not a big deal.
export function useAuth() {
  return useSelector((state) => state.authReducer);
}

type AuthType = 'home' | 'user';

export interface CheckAuthOptions {
  type: AuthType | AuthType[];
  auth: AuthReducerState;
}

export function checkAuth(options: CheckAuthOptions): boolean {
  const { isHome, loggedInWithCredentials } = options.auth;

  const types = Array.isArray(options.type) ? options.type : [options.type];
  return types
    .map((type) => {
      return type === 'home' ? isHome : loggedInWithCredentials;
    })
    .some(Boolean);
}

interface AuthGuardOptions<T, F> {
  type: AuthType | AuthType[];
  callback: () => Promise<T[]>;
  fallback: F;
}

/**
 * Returns the result of the callback if the user is authenticated.
 * Otherwise, returns the fallback value.
 *
 * @param options The options for the auth guard.
 * @param options.type The type of authentication required. If an array, the user must be authenticated with at least one of the types.
 * @param options.callback The callback to run if the user is authenticated.
 * @param options.fallback The fallback value to return if the user is not authenticated.
 */
export function authGuard<T, F>(options: AuthGuardOptions<T, F>): Promise<T[]> | Promise<F> {
  const auth = getAuth();

  const isAuth = checkAuth({ type: options.type, auth });
  const types = Array.isArray(options.type) ? options.type : [options.type];

  if (!isAuth) {
    warn(`[AuthGuard]: User is not authenticated as ${types.join('/')}.`);
    return Promise.resolve(options.fallback);
  }

  try {
    return options.callback();
  } catch (error) {
    return Promise.resolve(options.fallback);
  }
}
